import {
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './cn';

/** The shared text-control look; exported for the odd bespoke input. */
export const inputClass =
  'w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400';

export function Labeled({
  label,
  hint,
  className,
  children,
}: {
  label: ReactNode;
  /** Small helper line under the control. */
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn('block', className)}>
      <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

export function TextInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(inputClass, className)} {...props} />;
}

export function TextArea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(inputClass, className)} {...props} />;
}

export interface NumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  /** The committed number; `null`/`undefined` = empty. */
  value?: number | null;
  /**
   * Fires with the parsed number, or `null` while the field is empty or holds
   * something that isn't a number yet (`-`, `1.`). Callers decide what an empty
   * field means — the input never substitutes 0 on its own.
   */
  onValueChange?: (value: number | null) => void;
}

const fmt = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '' : String(v));
const parse = (text: string): number | null => {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/**
 * A number field that lets you clear it mid-typing. The DOM text is local
 * state; the parent only hears about it through `onValueChange`, so deleting
 * "12" to type "15" passes through `null` rather than snapping to 0 or 1.
 * When the committed `value` changes from outside (a drag on the canvas), the
 * text follows it; on blur the text is re-synced to whatever the parent kept.
 */
export function NumberInput({ value, onValueChange, className, onBlur, ...rest }: NumberInputProps) {
  const [text, setText] = useState(() => fmt(value));
  const [prev, setPrev] = useState(value);
  if (value !== prev) {
    // Adjusting state during render is React's sanctioned way to derive from
    // a changed prop without an extra effect-driven render.
    setPrev(value);
    if (parse(text) !== (value ?? null)) setText(fmt(value));
  }
  return (
    <input
      type="number"
      className={cn(inputClass, className)}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onValueChange?.(parse(e.target.value));
      }}
      onBlur={(e) => {
        setText(fmt(value));
        onBlur?.(e);
      }}
      {...rest}
    />
  );
}

export function Select({
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  return (
    <select className={cn(inputClass, className)} {...props}>
      {children}
    </select>
  );
}

/**
 * An on/off switch with a text label (and optional description) to its left.
 * `role="switch"` so screen readers announce it as one, and `type="button"` so
 * it never submits the form it sits in.
 */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  className,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label
      className={cn(
        'flex items-start justify-between gap-4 py-1',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
    >
      <span className="min-w-0">
        <span className={cn('block text-sm', description ? 'font-medium text-slate-800' : 'text-slate-700')}>
          {label}
        </span>
        {description && <span className="block text-xs text-slate-400">{description}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50',
          checked ? 'bg-brand-600' : 'bg-slate-300',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0.5',
          )}
        />
      </button>
    </label>
  );
}

/**
 * Selection checkbox.
 *
 * Two details it exists to encapsulate:
 * - `indeterminate` is a DOM *property* with no HTML attribute, so React can't
 *   set it from JSX — it has to be written onto the node after each render.
 * - The change is reported from `onClick`, not `onChange`, because only the
 *   mouse event carries `shiftKey` (and a keyboard Space on a checkbox fires a
 *   click too, so nothing is lost).
 *
 * Clicks are stopped here: every one of these sits inside a row or card that
 * navigates on click, and ticking a box must never open the thing.
 */
export function Checkbox({
  checked,
  indeterminate = false,
  onToggle,
  label,
  className,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onToggle: (opts: { shift: boolean }) => void;
  label: string;
  className?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onToggle({ shift: e.shiftKey });
      }}
      // React demands a handler on a controlled input; the click above is what
      // actually drives the state.
      onChange={() => undefined}
      className={cn(
        'h-4 w-4 cursor-pointer rounded border-slate-300 text-brand-600 accent-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1',
        className,
      )}
    />
  );
}
